import React, { useState, useEffect } from 'react';
import { Grid, Box, Button, Avatar, Typography } from '@mui/material';
import { colors, fonts, spacing, borderRadius } from '../../../constants';
import { getUserInitials, getUserDisplayName } from '../../../utils/queryDisplayUtils';
import SectionCard from './SectionCard';
import FieldLabel from './FieldLabel';
import FieldValue from './FieldValue';

/**
 * AssignmentInfoSection Component
 * Displays assignment information (raised by, assigned to, assigned by)
 * 
 * @param {Object} query - Query object
 * @param {boolean} hasRespondAccess - Whether user has respond access
 * @param {boolean} isEscalatedViewOnly - Whether user has view-only access
 * @param {boolean} isSubmittedStatus - Whether query is in submitted status
 * @param {boolean} isClosed - Whether query is closed
 * @param {string} normalizedUserRole - Normalized user role
 * @param {boolean} isFromEscalatedRoute - Whether viewing from escalated route
 * @param {function} onDelegateClick - Handler for delegate button click
 * @param {string} assigningId - ID of query being assigned
 */
const AssignmentInfoSection = ({
  query,
  hasRespondAccess,
  isEscalatedViewOnly,
  isSubmittedStatus,
  isClosed,
  normalizedUserRole,
  isFromEscalatedRoute,
  onDelegateClick,
  assigningId,
}) => {
  const [accessRights, setAccessRights] = useState({});

  useEffect(() => {
    try {
      const rights = JSON.parse(localStorage.getItem("AccessRights")) || {};
      setAccessRights(rights);
    } catch {
      setAccessRights({});
    }
  }, []);

  const showDelegateButton = accessRights["1040"] === "Y";

  if (!query) return null;

  const raisedByName = getUserDisplayName(query.raisedBy);
  const assignedToName = getUserDisplayName(query.assignee);
  const assignedByName = getUserDisplayName(query.assignor);

  return (
    <SectionCard title="Assignment Information">
      <Grid container spacing={2} sx={{ width: '100%', margin: 0 }}>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <FieldLabel label="Raised By" />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: fonts.fontSizeXs,
                  bgcolor: colors.primary,
              color: colors.textWhite,
                  fontWeight: fonts.weightSemiBold,
                }}
              >
                {getUserInitials(raisedByName)}
              </Avatar>
              <FieldValue value={raisedByName} />
            </Box>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <FieldLabel label="Assigned To" />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
              {assignedToName ? (
                <>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: fonts.fontSizeXs,
                      bgcolor: colors.primary,
                  color: colors.textWhite,
                      fontWeight: fonts.weightSemiBold,
                    }}
                  >
                    {getUserInitials(assignedToName)}
                  </Avatar>
                  <FieldValue value={assignedToName} />
                </>
              ) : (
                <FieldValue value="Unassigned" />
              )}
            </Box>
          </Box>
        </Grid>
        <Grid size={{ xs: 12, sm: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <FieldLabel label="Assigned By" />
            <Box sx={{ display: 'flex', alignItems: 'center', gap: spacing.xs }}>
              {assignedByName ? (
                <>
                  <Avatar
                    sx={{
                      width: 32,
                      height: 32,
                      fontSize: fonts.fontSizeXs,
                      bgcolor: colors.primary,
                  color: colors.textWhite,
                      fontWeight: fonts.weightSemiBold,
                    }}
                  >
                    {getUserInitials(assignedByName)}
                  </Avatar>
                  <FieldValue value={assignedByName} />
                </>
              ) : (
                <FieldValue value="N/A" />
              )}
            </Box>
          </Box>
        </Grid>
        {showDelegateButton &&
          hasRespondAccess &&
          !isEscalatedViewOnly &&
          !isSubmittedStatus &&
          !isClosed &&
          normalizedUserRole !== 'ADMIN' &&
          normalizedUserRole !== 'CH' &&
          !isFromEscalatedRoute && (
            <Grid size={{ xs: 12 }}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: spacing.xs }}>
                <Button
                  onClick={() => onDelegateClick(query)}
                  variant="contained"
                  disabled={assigningId === query._id}
                  sx={{
                    textTransform: 'none',
                    borderRadius: borderRadius.button,
                    padding: `${spacing.xs} ${spacing.md}`,
                    minWidth: '120px',
                    background: colors.primaryGradient,
                    color: `${colors.textWhite} !important`,
                    fontSize: fonts.fontSizeSm,
                    fontWeight: fonts.weightSemiBold,
                    boxShadow: `0 4px 12px ${colors.primaryShadow}`,
                    '&:hover': {
                      background: colors.primaryGradientHover,
                      boxShadow: `0 6px 16px ${colors.primaryShadowHover}`,
                    },
                    '&.Mui-disabled': {
                      background: colors.borderLight,
                      color: `${colors.textSecondary} !important`,
                      boxShadow: 'none',
                      opacity: 1,
                    },
                  }}
                >
                  {assigningId === query._id ? 'Delegating...' : 'Delegate Query'}
                </Button>
              </Box>
            </Grid>
          )}
      </Grid>
    </SectionCard>
  );
};

export default AssignmentInfoSection;
